import { AspectRatioValue } from '@/components/AspectRatioDropdown';
import BootstrapDialog from '@/components/Dialog/BootstrapDialog';
import { useAllVoices } from '@/hooks/useAllVoices';
import { useAnalytics } from '@/services/analytics';
import { DIALOG_TRANSITION_DURATION, useTemplateGalleryModal } from '@/state/template-gallery/useTemplateGalleryModal';
import { IProjectTemplate } from '@d-id/types';
import styled from '@emotion/styled';
import { useCallback, useEffect, useState } from 'react';
import { TemplateGalleryList } from './TemplateGalleryList';
import { TemplateGalleryPreview } from './TemplateGalleryPreview';

const StyledDialog = styled(BootstrapDialog)`
    & .MuiPaper-root {
        box-sizing: content-box;
        max-height: 960px;
        max-width: 1300px;
        border-radius: 20px;
        height: 90%;

        @media (max-height: 780px) {
            margin: 120px 0;
        }
    }
`;

interface Props {
    from?: 'Home' | 'Videos';
}

export const TemplateGalleryModal = ({ from }: Props) => {
    const mixpanel = useAnalytics();
    const { isOpen, close } = useTemplateGalleryModal();
    const [selectedTemplate, setSelectedTemplate] = useState<IProjectTemplate | null>(null);
    const [selectedTemplateIndex, setSelectedTemplateIndex] = useState<number | null>(null);
    const [aspectRatio, setAspectRatio] = useState<AspectRatioValue>('all');
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('');

    useAllVoices();

    const handleBack = useCallback(() => {
        setSelectedTemplate(null);
        setSelectedTemplateIndex(null);
    }, []);

    const handleTemplateClick = useCallback((template: IProjectTemplate | null, index: number | null) => {
        setSelectedTemplate(template);
        setSelectedTemplateIndex(index);
    }, []);

    useEffect(() => {
        handleBack();
    }, [handleBack, isOpen]);

    useEffect(() => {
        if (isOpen) {
            mixpanel?.track('templates-gallery', { event: 'view', componentType: 'modal' });
        }
    }, [isOpen]);

    return (
        <StyledDialog transitionDuration={DIALOG_TRANSITION_DURATION} width="80%" maxWidth="lg" open={isOpen} onClose={close}>
            <TemplateGalleryList
                setSelectedTemplate={handleTemplateClick}
                aspectRatio={aspectRatio}
                setAspectRatio={setAspectRatio}
                search={search}
                setSearch={setSearch}
                filter={filter}
                setFilter={setFilter}
                from={from}
            />
            {selectedTemplate && selectedTemplateIndex !== null && (
                <TemplateGalleryPreview
                    selectedTemplate={selectedTemplate}
                    onBack={handleBack}
                    aspectRatio={aspectRatio}
                    from={from}
                    search={search}
                    filter={filter}
                    selectedTemplateIndex={selectedTemplateIndex}
                />
            )}
        </StyledDialog>
    );
};
